import {
  type LinePattern,
  type Point,
  quadraticBezierCurve,
} from '@ipfabric/utils';
import { pipe } from 'fp-ts/lib/function';

const MAX_SEGMENT_LENGTH = 10;
const MIN_SEGMENTS = 8;
const MAX_SEGMENTS = 2048;

const getBaseSegmentsCount = (length: number, defaultSegments = 20): number => {
  if (!!length || Number.isNaN(length)) {
    return defaultSegments;
  }

  let baseSegmentsCount = Math.ceil(length / MAX_SEGMENT_LENGTH);

  if (baseSegmentsCount < MIN_SEGMENTS) {
    baseSegmentsCount = MIN_SEGMENTS;
  } else if (baseSegmentsCount > MAX_SEGMENTS) {
    baseSegmentsCount = MAX_SEGMENTS;
  }

  return baseSegmentsCount;
};

const makeMutiplySegmentsCountByLinePattern =
  (pattern: LinePattern, thickness: number) =>
  (count: number): number => {
    if (pattern === 'dotted') {
      return Math.round((count * 4) / thickness);
    }
    if (pattern === 'dashed') {
      return Math.round((count * 8) / thickness);
    }
    return count;
  };

/**
 * Base on http://www.malczak.linuxpl.com/blog/quadratic-bezier-curve-length/
 */
const getCurveLength = (
  fromX: number,
  fromY: number,
  cpX: number,
  cpY: number,
  toX: number,
  toY: number,
): number => {
  const ax = fromX - 2.0 * cpX + toX;
  const ay = fromY - 2.0 * cpY + toY;
  const bx = 2.0 * cpX - 2.0 * fromX;
  const by = 2.0 * cpY - 2.0 * fromY;
  const a = 4.0 * (ax * ax + ay * ay);
  const b = 4.0 * (ax * bx + ay * by);
  const c = bx * bx + by * by;

  const s = 2.0 * Math.sqrt(a + b + c);
  const a2 = Math.sqrt(a);
  const a32 = 2.0 * a * a2;
  const c2 = 2.0 * Math.sqrt(c);
  const ba = b / a2;

  return (
    (a32 * s +
      a2 * b * (s - c2) +
      (4.0 * c * a - b * b) * Math.log((2.0 * a2 + ba + s) / (ba + c2))) /
    (4.0 * a32)
  );
};

/**
 * Calculate points for a quadratic bezier curve.
 */
export const getQuadraticCurve = (
  start: Point,
  control: Point,
  end: Point,
  pattern: LinePattern,
  thickness: number,
): Point[] => {
  const segmentsCount = pipe(
    getCurveLength(start.x, start.y, control.x, control.y, end.x, end.y),
    getBaseSegmentsCount,
    makeMutiplySegmentsCountByLinePattern(pattern, thickness),
  );

  const curve = quadraticBezierCurve.create(start, control, end);
  const points = Array.from({ length: segmentsCount }, (_, index: number) =>
    quadraticBezierCurve.getPoint(curve, (index + 1) / segmentsCount),
  );

  return points;
};
